import { useState } from "react";
import { ILead, LeadSource } from "../../models/Lead";
import LeadContactDetails from "../contacts/LeadContactDetails";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import EnginesWorldwideDetails from "../vehicles/EnginesWorldwideDetails";
import VehicleDetails from "../vehicles/VehicleDetails";
import VehicleLocation from "../vehicles/VehicleLocation";
import DriverNotes from "./LeadInformation/DriverNotes";
import LeadIdentityImages from "./LeadInformation/LeadIdentityImages";
import LeadPaymentDetails from "./LeadInformation/LeadPaymentDetails";
import LeadStatusCard from "./LeadInformation/LeadStatusCard";
import OfficeNotes from "./LeadInformation/OfficeNotes";
import QuoteCalculationCard from "./LeadInformation/QuoteCalculationCard";
import VehicleNotes from "./LeadInformation/VehicleNotes";
import WidgetQuoteDetails from "./LeadInformation/WidgetQuoteDetails";
import useSingleLead from "../../data/useSingleLead";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useNotification from "../notifications/useNotifications";
import { useNavigate } from "react-router-dom";
import getAddress from "../../utils/getAddress";
import LeadNotes from "./LeadInformation/LeadNotes";


export default function LeadTab({ lead, setOpen }: { lead: ILead, setOpen?: (open: boolean) => void }) {
  const navigate = useNavigate();
  const { remove } = useSingleLead(lead.id, "LeadTab");
  const { deleteHandlers } = useDefaultCRUDHandlers("Lead");
  const { addNotification } = useNotification();
  const [deleteOpen, setDeleteOpen] = useState(false);

  const handleDelete = () => {
    remove.mutate(lead.id!, {
      onSuccess: () => {
        deleteHandlers.onSuccess();
        if (setOpen) {
          setOpen(false);
        } else {
          navigate("/leads");
        }
      },
      onError: (error) => {
        console.log(error);
        addNotification({
          variant: "error",
          primaryText: error,
        });
      },
    });
  };

  return (
    <>
      <ConfirmModal
        title="Delete Lead?"
        message="Are you sure you want to delete this lead? This is a permanent action."
        confirmButtonText={null}
        onConfirm={handleDelete}
        open={deleteOpen}
        setOpen={setDeleteOpen}
        isLoading={false}
      />
      <div className="mt-4 flex flex-col md:grid md:grid-cols-3 gap-6">
        {/* Left hand thin column */}
        <div className="col-span-1 gap-6 flex flex-col">
          <div className="col-span-1 bg-white rounded-lg shadow">
            <iframe
              className="min-h-[50vh]"
              title="Google map"
              style={{ border: 0, borderRadius: "0.5rem" }}
              loading="lazy"
              src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyAJWEQovZtn6KkghJ5z6maiL0G6KHjwbS4&q=${getAddress(
                lead?.vehicle
              )}&zoom=9`}
              width="100%"
              height="100%"
            ></iframe>
          </div>

          <VehicleDetails key={`vehicle-details-${lead?.vehicle?.id ?? lead?.id}`} vehicle={lead?.vehicle!} />
          {lead?.vehicle?.enginesWorldwide.map((offer) => (
            <EnginesWorldwideDetails key={offer.id} offer={offer} />
          ))}

          {/* Calculation Breakdown */}
          {(lead?.website == LeadSource.SellAVehicle || lead?.website == LeadSource.Widget) && (
            <>
              <QuoteCalculationCard key={`quote-calc-${lead.id}`} leadId={lead.id!} quoteType={lead.quoteType!} isCollection={lead.isCollection!} total={lead.price!} />
              <WidgetQuoteDetails key={`widget-quote-${lead.id}`} leadId={lead.id!} />
            </>
          )}

        </div>

        {/* Right hand wide column */}
        <div className="col-span-2 gap-6 flex flex-col">
          <LeadContactDetails key={`contact-details-${lead.id}`} lead={lead!} />
          <VehicleLocation key={`vehicle-location-${lead?.vehicle?.id ?? lead?.id}`} vehicle={lead?.vehicle!} />
          <LeadStatusCard key={`lead-status-${lead.id}`} leadId={lead?.id!} />
          <LeadIdentityImages key={`lead-images-${lead.id}`} lead={lead!} />
          <LeadPaymentDetails key={`lead-payment-${lead.id}`} lead={lead!} />
        </div>

        <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-4 md:gap-x-4 gap-y-4">
          <LeadNotes key={`office-notes-${lead.id}`} lead={lead!} notesProp="officeNotes" title="Office Notes" />
          <LeadNotes key={`driver-notes-${lead.id}`} lead={lead!} notesProp="driverNotes" title="Driver Notes" />
          <LeadNotes key={`yard-notes-${lead.id}`} lead={lead!} notesProp="yardNotes" title="Yard Notes" />
          <VehicleNotes key={`vehicle-notes-${lead?.vehicle?.id ?? lead.id}`} vehicle={lead.vehicle!} />
        </div>

        <div className="md:col-span-3">
          <button
            className="whitespace-nowrap float-right rounded-md border border-transparent bg-red-600 px-3 py-3 text-sm font-medium leading-4 text-white shadow-sm hover:bg-red-700"
            type="button"
            onClick={() => setDeleteOpen(true)}
          >
            Delete Lead
          </button>
        </div>
      </div>
    </>
  )

}